import { useState } from 'react';

import type { HousekeepingPreparation } from '../../../pages/housekeeping/types/housekeeping';
import { useCocherPreparation } from '../../api/useActionsMenage';
import { useTraductionsMetier } from '../../api/useTraductionsMetier';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';

import CaseACocher from './CaseACocher';

/**
 * Les préparations du jour (ventes à préparer), cochées une à une — comme
 * `PreparationsBlock` du web : une ligne envoyée reste cochée, et redevient libre
 * si le serveur la refuse. L'échec est dit EN LIGNE, pas par un toast.
 *
 * Une tâche de règle de séjour à venir (prévision d'un jour futur) n'a pas encore
 * d'`id` : elle se lit mais ne se coche pas, il n'y a rien à enregistrer.
 */
export default function BlocPreparations({ preparations }: { preparations: HousekeepingPreparation[] }) {
    const cocher = useCocherPreparation();
    // Suivies une à une : des appuis rapprochés sur plusieurs lignes ne se perdent pas.
    const [envoyees, setEnvoyees] = useState<Set<number>>(() => new Set());
    const [echec, setEchec] = useState(false);
    const traductions = useTraductionsMetier(preparations.map((p) => p.name));

    if (preparations.length === 0) return null;

    const basculer = (preparation: HousekeepingPreparation) => {
        const id = preparation.id;
        if (id === null || preparation.status === 'completed' || envoyees.has(id)) return;
        setEchec(false);
        setEnvoyees((avant) => new Set(avant).add(id));
        cocher.mutateAsync({ id }).catch(() => {
            setEnvoyees((avant) => {
                const apres = new Set(avant);
                apres.delete(id);
                return apres;
            });
            setEchec(true);
        });
    };

    return (
        <section className="flex flex-col gap-2">
            <Text variante="sousTitre" as="h2">
                {t('menage.preparations.titre')}
            </Text>
            {echec && (
                <div role="alert">
                    <Text variante="petit" ton="danger">
                        {t('menage.preparations.echec')}
                    </Text>
                </div>
            )}
            {preparations.map((preparation, index) => {
                const aVenir = preparation.id === null;
                const faite = preparation.status === 'completed' || (!aVenir && envoyees.has(preparation.id as number));
                return (
                    <CaseACocher
                        key={aVenir ? `a-venir-${preparation.stay_rule_id ?? 'x'}-${index}` : preparation.id}
                        libelle={`${traductions.libelle(preparation.name) ?? preparation.name} ×${preparation.quantity}`}
                        original={traductions.libelle(preparation.name) ? preparation.name : null}
                        detail={preparation.instructions}
                        coche={faite}
                        desactive={faite || aVenir}
                        onChange={() => basculer(preparation)}
                    />
                );
            })}
        </section>
    );
}
